import type {
  BrowserCommand,
  BrowserSessionSnapshot,
  BrowserTabSnapshot,
  DevTunnelRecord,
  OrchestrationThreadActivity,
} from "@cmmd-forge/contracts";
import { normalizeBrowserError } from "@cmmd-forge/shared/browserErrors";
import { devTunnelIdFromUrl } from "@cmmd-forge/shared/devTunnelUrl";

import { localDevServerPreviewPort, normalizePreviewUrl } from "~/lib/devServerPreviewUrl";
import {
  browserDisplayUrl,
  isBlankBrowserUrl,
  isDevTunnelPreviewUrl,
  isLocalPreviewBrowserUrl,
  readDevTunnelMetadata,
} from "./BrowserPreviewPanel.url";

const RECOVERABLE_DEV_PREVIEW_FAILURE_PATTERN =
  /\b(?:502|bad gateway|failed to reach dev tunnel target|failed to reach local dev server)\b/iu;
const LOCAL_PREVIEW_BROWSER_SESSION_ATTEMPTS = 3;

export function shouldRecoverLocalPreviewBrowserError(input: {
  readonly url: string;
  readonly error: unknown;
  readonly devTunnelRecords: ReadonlyArray<DevTunnelRecord>;
}): boolean {
  const normalized = normalizeBrowserError(input.error);
  if (
    normalized.title === "Browser navigation blocked" ||
    normalized.title === "Browser session unavailable"
  ) {
    return false;
  }

  return isLocalPreviewBrowserUrl(input.url, input.devTunnelRecords);
}

export function shouldRetryLocalPreviewSessionStartup(input: {
  readonly url: string;
  readonly error: unknown;
  readonly devTunnelRecords: ReadonlyArray<DevTunnelRecord>;
}): boolean {
  const normalized = normalizeBrowserError(input.error);
  if (normalized.title === "Browser navigation blocked") {
    return false;
  }

  return isLocalPreviewBrowserUrl(input.url, input.devTunnelRecords);
}

export function localPreviewSessionAttemptCount(
  url: string,
  devTunnelRecords: ReadonlyArray<DevTunnelRecord>,
): number {
  return isLocalPreviewBrowserUrl(url, devTunnelRecords)
    ? LOCAL_PREVIEW_BROWSER_SESSION_ATTEMPTS
    : 1;
}

export function isRecoverableDevTunnelFailure(tab: BrowserTabSnapshot): boolean {
  return (
    Boolean(tab.url && isDevTunnelPreviewUrl(tab.url)) &&
    Boolean(tab.title && RECOVERABLE_DEV_PREVIEW_FAILURE_PATTERN.test(tab.title))
  );
}

export function isRecoverableLocalPreviewFailure(
  tab: BrowserTabSnapshot,
  devTunnelRecords: ReadonlyArray<DevTunnelRecord> = [],
): boolean {
  if (!isRecoverableDevTunnelFailure(tab)) {
    return false;
  }
  const displayUrl = browserDisplayUrl(tab.url ?? null, devTunnelRecords) ?? tab.url;
  return Boolean(displayUrl && localDevServerPreviewPort(displayUrl));
}

export function isExpiredDevTunnelTab(
  tab: BrowserTabSnapshot,
  devTunnelRecords: ReadonlyArray<DevTunnelRecord> = [],
  nowMs: number = Date.now(),
): boolean {
  if (!tab.url || !isDevTunnelPreviewUrl(tab.url)) {
    return false;
  }
  const expiresAt = readDevTunnelMetadata(tab.url, devTunnelRecords)?.expiresAt;
  if (!expiresAt) {
    return true;
  }
  const expiresAtMs = Date.parse(expiresAt);
  return !Number.isFinite(expiresAtMs) || expiresAtMs <= nowMs;
}

export function visibleBrowserTabs(
  tabs: readonly BrowserTabSnapshot[],
  devTunnelRecords: ReadonlyArray<DevTunnelRecord> = [],
): BrowserTabSnapshot[] {
  return tabs.filter(
    (tab) =>
      !isExpiredDevTunnelTab(tab, devTunnelRecords) &&
      !isRecoverableLocalPreviewFailure(tab, devTunnelRecords),
  );
}

export function recoverableLocalPreviewDisplayUrl(
  tabs: readonly BrowserTabSnapshot[],
  devTunnelRecords: ReadonlyArray<DevTunnelRecord> = [],
): string | null {
  for (const tab of tabs) {
    if (!isRecoverableLocalPreviewFailure(tab, devTunnelRecords)) {
      continue;
    }
    const displayUrl = browserDisplayUrl(tab.url ?? null, devTunnelRecords);
    if (displayUrl && localDevServerPreviewPort(displayUrl)) {
      return displayUrl;
    }
  }
  return null;
}

export function browserSessionHasRecoverableLocalPreviewPort(
  input: BrowserSessionSnapshot | null,
  port: number,
  devTunnelRecords: ReadonlyArray<DevTunnelRecord> = [],
): boolean {
  return Boolean(
    input?.tabs.some((tab) => {
      if (
        !isExpiredDevTunnelTab(tab, devTunnelRecords) &&
        !isRecoverableLocalPreviewFailure(tab, devTunnelRecords)
      ) {
        return false;
      }
      const displayUrl = browserDisplayUrl(tab.url ?? null, devTunnelRecords) ?? tab.url;
      return Boolean(displayUrl && localDevServerPreviewPort(displayUrl) === port);
    }),
  );
}

export function browserSessionHasDifferentDevTunnelForLocalPreview(
  input: BrowserSessionSnapshot | null,
  commandUrl: string,
  devTunnelRecords: ReadonlyArray<DevTunnelRecord> = [],
): boolean {
  if (!isDevTunnelPreviewUrl(commandUrl)) {
    return false;
  }
  const commandTunnelId = devTunnelIdFromUrl(commandUrl);
  const commandPort = readDevTunnelMetadata(commandUrl, devTunnelRecords)?.localPort;
  if (!commandTunnelId || !commandPort) {
    return false;
  }
  return Boolean(
    input?.tabs.some((tab) => {
      if (!tab.url || !isDevTunnelPreviewUrl(tab.url)) {
        return false;
      }
      const tabTunnelId = devTunnelIdFromUrl(tab.url);
      const tabPort = readDevTunnelMetadata(tab.url, devTunnelRecords)?.localPort;
      return Boolean(tabTunnelId && tabTunnelId !== commandTunnelId && tabPort === commandPort);
    }),
  );
}

export function browserSessionHasAddressUrl(
  input: BrowserSessionSnapshot,
  rawUrl: string,
  devTunnelRecords: ReadonlyArray<DevTunnelRecord> = [],
): boolean {
  const expected = new Set<string>();
  const addExpected = (value: string | null | undefined) => {
    const normalized = value ? normalizePreviewUrl(value) : null;
    if (normalized) {
      expected.add(normalized);
    }
  };
  addExpected(rawUrl);
  addExpected(browserDisplayUrl(rawUrl, devTunnelRecords));
  if (expected.size === 0) {
    return false;
  }

  return input.tabs.some((tab) => {
    if (isBlankBrowserUrl(tab.url)) {
      return false;
    }
    const tabUrl = normalizePreviewUrl(tab.url ?? null);
    const tabDisplay = browserDisplayUrl(tab.url ?? null, devTunnelRecords);
    const tabDisplayUrl = tabDisplay ? normalizePreviewUrl(tabDisplay) : null;
    return Boolean(
      (tabUrl && expected.has(tabUrl)) || (tabDisplayUrl && expected.has(tabDisplayUrl)),
    );
  });
}

export function browserSessionHasVisibleAddressUrl(
  input: BrowserSessionSnapshot,
  rawUrl: string,
  devTunnelRecords: ReadonlyArray<DevTunnelRecord> = [],
): boolean {
  return browserSessionHasAddressUrl(
    {
      ...input,
      tabs: visibleBrowserTabs(input.tabs, devTunnelRecords),
    },
    rawUrl,
    devTunnelRecords,
  );
}

function browserTabWithPendingRequestedUrl(tab: BrowserTabSnapshot): BrowserTabSnapshot {
  return tab.navigationStatus === "pending" && tab.requestedUrl
    ? { ...tab, url: tab.requestedUrl }
    : tab;
}

export function browserSessionHasVisibleAddressIntent(
  input: BrowserSessionSnapshot,
  rawUrl: string,
  devTunnelRecords: ReadonlyArray<DevTunnelRecord> = [],
): boolean {
  const visibleTabs = visibleBrowserTabs(input.tabs, devTunnelRecords);
  return browserSessionHasAddressUrl(
    {
      ...input,
      tabs: visibleTabs.map(browserTabWithPendingRequestedUrl),
    },
    rawUrl,
    devTunnelRecords,
  );
}

export function preserveLocalPreviewTabsForMaintenanceCommandWithStrategy(input: {
  readonly command: BrowserCommand;
  readonly commandCanChangeActiveAddress: (command: BrowserCommand) => boolean;
  readonly previousSession: BrowserSessionSnapshot | null;
  readonly nextSession: BrowserSessionSnapshot;
  readonly devTunnelRecords: ReadonlyArray<DevTunnelRecord>;
}): BrowserSessionSnapshot {
  if (input.commandCanChangeActiveAddress(input.command) || !input.previousSession) {
    return input.nextSession;
  }
  const previousSession = input.previousSession;

  const previousActiveTab =
    previousSession.tabs.find((tab) => tab.tabId === previousSession.activeTabId) ??
    previousSession.tabs[0] ??
    null;
  const previousDisplayUrl =
    browserDisplayUrl(previousActiveTab?.url ?? null, input.devTunnelRecords) ??
    previousActiveTab?.url ??
    null;

  if (
    !previousDisplayUrl ||
    !isLocalPreviewBrowserUrl(previousDisplayUrl, input.devTunnelRecords)
  ) {
    return input.nextSession;
  }
  if (browserSessionHasAddressUrl(input.nextSession, previousDisplayUrl, input.devTunnelRecords)) {
    return input.nextSession;
  }

  return {
    ...input.nextSession,
    activeTabId: previousSession.activeTabId,
    tabs: previousSession.tabs,
  };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asTrimmedString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function compareThreadActivities(
  left: OrchestrationThreadActivity,
  right: OrchestrationThreadActivity,
): number {
  if (left.sequence !== undefined && right.sequence !== undefined) {
    if (left.sequence !== right.sequence) {
      return left.sequence - right.sequence;
    }
  } else if (left.sequence !== undefined) {
    return 1;
  } else if (right.sequence !== undefined) {
    return -1;
  }
  if (left.createdAt !== right.createdAt) {
    return left.createdAt.localeCompare(right.createdAt);
  }
  return left.id.localeCompare(right.id);
}

function extractBrowserToolPayload(input: {
  readonly payload: Record<string, unknown> | null;
  readonly summary?: string;
}): {
  readonly actionHint: string;
  readonly input: Record<string, unknown> | null;
} | null {
  const payload = input.payload;
  if (!payload) {
    return null;
  }
  const data = asRecord(payload.data);
  const item = asRecord(data?.item);
  const itemArguments = asRecord(item?.arguments);
  const args = asRecord(data?.args);
  const nestedArguments = asRecord(args?.arguments);
  const resolvedInput =
    asRecord(item?.input) ??
    itemArguments ??
    asRecord(data?.input) ??
    nestedArguments ??
    asRecord(args?.input) ??
    args;
  const title = asTrimmedString(payload.title);
  const itemType = asTrimmedString(payload.itemType);
  const toolName =
    asTrimmedString(item?.name) ??
    asTrimmedString(item?.tool) ??
    asTrimmedString(item?.toolName) ??
    asTrimmedString(data?.name) ??
    asTrimmedString(args?.tool) ??
    asTrimmedString(args?.toolName) ??
    asTrimmedString(payload.toolName) ??
    title ??
    asTrimmedString(input.summary);
  const detail = asTrimmedString(payload.detail);
  const haystack = [toolName, title, input.summary, itemType, detail]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  if (!haystack.includes("browser") && !haystack.includes("forge-browser")) {
    return null;
  }
  return {
    actionHint: [
      asTrimmedString(resolvedInput?.type),
      asTrimmedString(resolvedInput?.command),
      toolName,
      detail,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase(),
    input: resolvedInput,
  };
}

function browserSessionHasMatchingHistoryUrl(
  session: BrowserSessionSnapshot,
  restoreUrl: string,
): boolean {
  return findRestorableHistoryTab({ session, restoreUrl }) !== null;
}

export function findRestorableHistoryTab(input: {
  readonly session: BrowserSessionSnapshot;
  readonly restoreUrl: string;
}): BrowserTabSnapshot | null {
  const normalizedRestoreUrl = normalizePreviewUrl(input.restoreUrl) ?? input.restoreUrl;
  return (
    input.session.tabs.find((tab) => {
      if (isBlankBrowserUrl(tab.url)) {
        return false;
      }
      const normalizedTabUrl = normalizePreviewUrl(tab.url ?? "") ?? tab.url;
      return normalizedTabUrl === normalizedRestoreUrl;
    }) ?? null
  );
}

export function selectPreferredPreviewTab(input: {
  readonly tabs: readonly BrowserTabSnapshot[];
  readonly routeTabId?: string | null | undefined;
  readonly activeTabId?: string | null | undefined;
}): BrowserTabSnapshot | null {
  const activeTab = input.activeTabId
    ? (input.tabs.find((tab) => tab.tabId === input.activeTabId) ?? null)
    : null;
  const routeTab = input.routeTabId
    ? (input.tabs.find((tab) => tab.tabId === input.routeTabId) ?? null)
    : null;
  if (routeTab) {
    if (!isBlankBrowserUrl(routeTab.url)) {
      return routeTab;
    }
    if (activeTab && !isBlankBrowserUrl(activeTab.url) && activeTab.tabId !== routeTab.tabId) {
      return activeTab;
    }
    return routeTab;
  }

  if (activeTab && !isBlankBrowserUrl(activeTab.url)) {
    return activeTab;
  }

  const firstNonBlankTab = input.tabs.find((tab) => !isBlankBrowserUrl(tab.url)) ?? null;
  if (firstNonBlankTab) {
    return firstNonBlankTab;
  }

  return activeTab ?? input.tabs[0] ?? null;
}

export function deriveRestorableThreadBrowserUrl(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
): string | null {
  const ordered = [...activities].toSorted(compareThreadActivities);
  for (let index = ordered.length - 1; index >= 0; index -= 1) {
    const activity = ordered[index];
    if (
      activity?.kind !== "tool.completed" &&
      activity?.kind !== "tool.updated" &&
      activity?.kind !== "tool.started"
    ) {
      continue;
    }
    const payload = extractBrowserToolPayload({
      payload: asRecord(activity.payload),
      summary: activity.summary,
    });
    if (!payload) {
      continue;
    }
    if (/(close[-_.\s]?session|reset[-_.\s]?session)/.test(payload.actionHint)) {
      return null;
    }
    const rawUrl =
      asTrimmedString(payload.input?.url) ?? asTrimmedString(payload.input?.initialUrl);
    if (!rawUrl || isBlankBrowserUrl(rawUrl)) {
      continue;
    }
    return normalizePreviewUrl(rawUrl) ?? rawUrl;
  }
  return null;
}

export function shouldRestoreBrowserSessionFromThreadHistory(input: {
  readonly restoreUrl: string | null;
  readonly session: BrowserSessionSnapshot | null;
}): boolean {
  if (!input.restoreUrl) {
    return false;
  }
  if (!input.session) {
    return true;
  }
  if (browserSessionHasMatchingHistoryUrl(input.session, input.restoreUrl)) {
    return false;
  }
  const activeTab =
    input.session.tabs.find((tab) => tab.tabId === input.session?.activeTabId) ??
    input.session.tabs[0] ??
    null;
  return !activeTab || isBlankBrowserUrl(activeTab.url);
}
